/**
 * After this many successful user prompts in a row, context is reset: the last
 * edited image becomes the new base URL and accumulated attachments are cleared.
 */
export const PROMPTS_CHECKPOINT_SIZE = 3;

/**
 * Normalizes a single edit-image turn from legacy string or object shape.
 */
export const normalizeEditImageTurn = (turn) => {
  if (typeof turn === "string") {
    return {
      prompt: turn,
      attachmentUrls: [],
    };
  }

  const normalized = {
    prompt: typeof turn?.prompt === "string" ? turn.prompt : "",
    attachmentUrls: Array.isArray(turn?.attachmentUrls)
      ? turn.attachmentUrls
      : [],
  };

  if (turn?.resultUrl) {
    normalized.resultUrl = turn.resultUrl;
  }

  return normalized;
};

/**
 * Normalizes edit-image prompts from payload snapshot or local checkpoint history.
 */
export const normalizeEditImagePrompts = (prompts) => {
  if (!Array.isArray(prompts)) {
    return [];
  }

  return prompts
    .map((turn) => normalizeEditImageTurn(turn))
    .filter((turn) => turn.prompt);
};

/**
 * Builds job payload turn objects (without resultUrl on the in-flight turn).
 */
export const buildEditImagePromptsForJob = ({
  history = [],
  prompt,
  attachmentUrls = [],
}) => {
  const safeHistory = normalizeEditImagePrompts(history).map(
    ({
      prompt: turnPrompt,
      attachmentUrls: turnAttachmentUrls,
      resultUrl,
    }) => ({
      prompt: turnPrompt,
      attachmentUrls: turnAttachmentUrls,
      ...(resultUrl ? { resultUrl } : {}),
    })
  );

  return [
    ...safeHistory,
    {
      prompt,
      attachmentUrls: Array.isArray(attachmentUrls) ? attachmentUrls : [],
    },
  ];
};

export const mergeAttachmentUrls = (previousUrls, incomingUrls) => {
  const merged = [...previousUrls];
  const safeIncoming = Array.isArray(incomingUrls) ? incomingUrls : [];

  for (const url of safeIncoming) {
    if (url && !merged.includes(url)) {
      merged.push(url);
    }
  }

  return merged;
};

export const mergeAttachmentFileNames = (previousNames, incomingNames) => ({
  ...previousNames,
  ...(incomingNames && typeof incomingNames === "object" ? incomingNames : {}),
});
